import {
  toast as reactToast,
  ToastContainer as ReactToastContainer
} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Icon, Icons, Size, TextIconColor } from '~/components/core';

export enum ToastType {
  ERROR = 'ERROR',
  REFRESH = 'REFRESH',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING'
}

// Constants
const TOAST_TYPE_STYLES = {
  [ToastType.SUCCESS]: 'bg-highlight text-primary-dark',
  [ToastType.ERROR]: 'bg-fuchsia-error text-primary-dark',
  [ToastType.WARNING]: 'bg-yellow-warning text-primary-dark',
  [ToastType.REFRESH]: 'bg-primary-bright text-white'
};

const TOAST_TYPE_ICON_COLORS = {
  [ToastType.SUCCESS]: TextIconColor.BLACK,
  [ToastType.ERROR]: TextIconColor.BLACK,
  [ToastType.WARNING]: TextIconColor.BLACK,
  [ToastType.REFRESH]: TextIconColor.PRIMARY
};

const TOAST_TYPE_ICONS = {
  [ToastType.SUCCESS]: Icon.CONFIRM,
  [ToastType.ERROR]: Icon.ERROR,
  [ToastType.WARNING]: Icon.WARNING,
  [ToastType.REFRESH]: Icon.RELOAD
};

// Custom styles for the toast container - to be added with our own CSS
const toastCSS = `
.toast-container.Toastify__toast-container {
  padding: 0;
  width: auto;
}

.toast-container .Toastify__toast {
  margin: 0 0 8px 0;
  padding: 0;
  background: none;
  box-shadow: none;
  border: none;
  width: auto;
}

.toast-container .Toastify__toast:last-child {
  margin-bottom: 0;
}

.toast-container .Toastify__toast-body {
  margin: 0;
  padding: 0;
  background: none;
  width: auto;
}

.toast-container .Toastify__close-button {
  display: none;
}
`;

// Add the custom toast CSS to the document once
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = toastCSS;
  document.head.appendChild(style);
}

export const Toast = () => (
  <ReactToastContainer
    bodyClassName="body"
    className="toast-container"
    closeButton={false}
    hideProgressBar
    position="top-right"
    progressClassName="progress"
    style={{ top: '4.5rem' }}
    toastClassName="toast"
  />
);

interface CustomToastProps {
  closeToast: (() => void) | undefined;
  message: string;
  type: ToastType;
}

const CustomToast = ({ message, type, closeToast }: CustomToastProps) => (
  <div
    className={`shadow-card h-16 min-w-fit rounded-lg ${TOAST_TYPE_STYLES[type]}`}
    data-testid="toast-wrapper"
  >
    <div className="flex h-full items-center px-6">
      <div className="flex flex-1 items-center justify-center gap-2.5">
        <Icons
          color={TOAST_TYPE_ICON_COLORS[type]}
          icon={TOAST_TYPE_ICONS[type]}
          size={Size.LARGE}
        />
        <span className="whitespace-nowrap text-base">{message}</span>
      </div>
      {closeToast && (
        <button
          aria-label="Close notification"
          className="ml-6 hover:opacity-80"
          onClick={() => closeToast()}
          type="button"
        >
          <Icons
            color={TOAST_TYPE_ICON_COLORS[type]}
            icon={Icon.CLOSE}
            size={Size.MEDIUM}
          />
        </button>
      )}
    </div>
  </div>
);

const getAutoClose = (type: ToastType, autoClose: boolean): number | false => {
  if (!autoClose) {
    return false;
  }

  switch (type) {
    case ToastType.ERROR:
      return 5000;
    case ToastType.REFRESH:
      return 7000;
    case ToastType.SUCCESS:
    case ToastType.WARNING:
    default:
      return 3000;
  }
};

export interface ToastArgs {
  message: string;
  type: ToastType;
  autoClose?: boolean;
  closeButton?: boolean;
  delay?: boolean;
  onClick?: () => void;
  preventClose?: boolean;
}

const useToast = () => {
  const enqueueToast = ({
    type,
    message,
    delay,
    autoClose = true,
    preventClose = false,
    onClick = () => {},
    closeButton = false
  }: ToastArgs) => {
    const showCloseButton = closeButton || (!autoClose && !preventClose);

    reactToast(
      ({ closeToast }) => (
        <CustomToast
          closeToast={showCloseButton ? closeToast : undefined}
          message={message}
          type={type}
        />
      ),
      {
        draggablePercent: 60,
        autoClose: getAutoClose(type, autoClose),
        hideProgressBar: true,
        closeButton: false,
        closeOnClick: showCloseButton,
        onClick,
        position: 'top-right',
        delay: delay ? 2000 : 0
      }
    );
  };

  return { enqueueToast };
};

export default useToast;
